import { useTradeContext } from "contexts/TradeContext";
import { useEffect, useState } from "react";
import { useSignalAbort } from "./useSignalAbort";
import { useQuery } from "@tanstack/react-query";
import axiosService from "services/axios";
import useUserStoreV2 from "store/user-store-v2/useUserStoreV2";
import useToastStore from "store/toast-store/useToastStore";
import useTradeStore from "store/trade-store.ts/useTradeStore";
import { usePortfolioContext } from "contexts/PortfolioContext";
import { InsufficientPriceHint } from "components/CrossChainSwapDrawerV2/priceHint";
import { getChainsByMode, Mode, TRADING_MODE_CONFIG_MAP } from "tradeConfigs";
import { Chain, CHAIN_CONFIG, EVM_CHAINS } from "chainConfigs";
import { NumpadKey } from "components/TradeDrawer";
import { useTxnSign } from "./useTxnSign";
import { SOLANA, TON, WRAPPED_NATIVE_TOKENS_ADDRESS } from "consts";
import { useLocation } from "react-router-dom";
export const useTradeDrawer = ({
  tgGroupId,
  isFromTrendingCall,
  defaultInputUsd,
  onConfirm,
  onProcessing,
  onProcessed,
  onClose,
  onFail,
  propsMode,
}: {
  tgGroupId?: number;
  isFromTrendingCall?: boolean;
  defaultInputUsd?: number;
  onConfirm?: (message?: string) => void;
  onProcessing?: () => void;
  onProcessed?: () => void;
  onClose?: () => void;
  onFail?: (message?: string) => void;
  propsMode?: Mode;
}) => {
  const {
    // Mode
    mode: tradeMode,
    // Native Token
    selectedNativeChain,
    nativeTokenPrice,
    nativeBalanceUsd,
    // Target Token
    selectedTargetChain,
    targetTokenConfig,
    targetTokenPrice,
    targetBalanceUsd,
    // Others
    senderAddress,
    isTrading,
    destinationAddress,
    refetchTargetTokenBalance,
    handleSetMode,
    handleSetIsTrading,
    targetBalanceAmount,
    // Polling
    setIsPolling,
    setIsTonBoardcasting,
    setTxnHash,
  } = useTradeContext();

  // location
  const location = useLocation();

  // user store
  const { isCustodial, preferences } = useUserStoreV2();
  const { validatorTip } = preferences;

  // Input Value
  const [inputUsdString, setInputUsdString] = useState("0"); // (USD)
  const [originalInputUsd, setOriginalInputUsd] = useState("0"); // Original USD before bridge fee deduction
  const [targetAmount, setTargetAmount] = useState(0); // (amount)
  const [nativeAmount, setNativeAmount] = useState(0); // (amount)
  const [gasBuffer, setGasBuffer] = useState(0);
  const [hasBridgeFeeApplied, setHasBridgeFeeApplied] = useState(false); // Flag to prevent bridge fee loop

  // Reminder Drawer
  const [isOpenConfirmDrawer, setIsOpenConfirmDrawer] = useState(false);

  // Flags
  const [isInsufficientBalance, setIsInsufficientBalance] = useState(false);
  const [isSellAll, setIsSellAll] = useState(false);
  const [isBuyAll, setIsBuyAll] = useState(false);
  const [isBalanceExceeded, setIsBalanceExceeded] = useState(false);

  // Toast
  const { showToast } = useToastStore();
  const [inputText, setInputText] = useState<string | undefined>(undefined);

  // Portfolio
  const { refetchUserAllPortfolioAssets } = usePortfolioContext();

  // Trade Store
  const { isLoading: isNativeBalanceLoading, updateUserBalance, getUserBalance } = useTradeStore();

  const { signAndBroadcastTxn, signAndBroadcastSolanaTxn } = useTxnSign();

  const {
    priceImpact,
    noValidRouteMessage,
    isLoadingPriceImpact,
    isRefetchingPriceImpact,
    refetchPriceImpact,
    bridgeFee,
  } = usePriceImpact({
    isSellAll,
    isBuyAll,
    targetAmount,
    nativeAmount,
  });

  const mode = tradeMode === "SWAP" ? "BUY" : tradeMode;
  const tradeModeConfig = TRADING_MODE_CONFIG_MAP[mode];

  const isTraddingWrappedToken =
    CHAIN_CONFIG[selectedTargetChain].wrappedTokenAddress ===
    targetTokenConfig[selectedTargetChain]?.token;

  // Init
  useEffect(() => {
    const initialValue =
      mode === "BUY" ? (defaultInputUsd ? defaultInputUsd.toString() : "0") : "0";
    setInputUsdString(initialValue);
    setOriginalInputUsd(initialValue);
    setHasBridgeFeeApplied(false); // Reset bridge fee flag on init

    setIsSellAll(false);
    setIsBuyAll(false);
    setIsInsufficientBalance(false);
  }, [mode, selectedNativeChain, defaultInputUsd]);

  // Update Input Amount from USD
  useEffect(() => {
    if (targetTokenPrice) {
      setTargetAmount(+inputUsdString / targetTokenPrice);
    }
    if (nativeTokenPrice) {
      setNativeAmount(+inputUsdString / nativeTokenPrice);
    }
  }, [inputUsdString, targetTokenPrice, nativeTokenPrice]);

  // Start Trading
  const handleConfirm = (slippage?: number) => {
    // Checks
    handleConfirmClick?.(slippage ?? 3);
    setIsOpenConfirmDrawer(false);
    handleSetMode("BUY");
  };

  // Confirm
  const handleOpenConfirmDrawer = (isOpen: boolean) => {
    setIsOpenConfirmDrawer(isOpen);
  };

  // Close Drawer
  const handleOnClose = () => {
    const resetValue = mode === "BUY" ? (defaultInputUsd ? defaultInputUsd.toString() : "0") : "0";
    setInputUsdString(resetValue);
    setOriginalInputUsd(resetValue);
    setHasBridgeFeeApplied(false); // Reset bridge fee flag on close
    setTargetAmount(0);
    setNativeAmount(0);
    setIsSellAll(false);
    setIsBuyAll(false);
    setIsInsufficientBalance(false);
    setGasBuffer(0);
    handleSetMode("BUY");
    onClose?.();
  };

  // Numpad Press
  const handleNumpadPress = (key: NumpadKey) => {
    setInputText(undefined);
    setIsSellAll(false);
    setIsBuyAll(false);
    setGasBuffer(0);

    let newValue;
    if (key === "delete") {
      newValue = "0";
    } else if (key === ".") {
      if (!originalInputUsd.includes(".")) {
        newValue = originalInputUsd + key;
      } else {
        return; // Don't add multiple decimals
      }
    } else {
      newValue = originalInputUsd + key;
    }

    setOriginalInputUsd(newValue);
    setInputUsdString(newValue);
    setHasBridgeFeeApplied(false); // Reset bridge fee flag when user inputs new value
    setIsInsufficientBalance(false);
  };

  // Percent Press
  const handlePercentPress = (percent: number) => {
    if (mode === "BUY") {
      let gasBuffer;
      if (percent === 1) {
        setIsBuyAll(true);
        gasBuffer =
          percent === 1
            ? selectedNativeChain === selectedTargetChain
              ? (tradeModeConfig.minimumGasBufferAmount![selectedNativeChain] ?? 0) *
                nativeTokenPrice!
              : (tradeModeConfig.minimumCrossChainGasBufferAmount![selectedNativeChain] ?? 0) *
                nativeTokenPrice!
            : 0;
        setGasBuffer(gasBuffer);
      } else {
        setIsBuyAll(false);
        setGasBuffer(0);
      }

      if (selectedNativeChain !== selectedTargetChain) {
        setInputText(`LOADING...`);
      }

      const MIN_AMOUNT = 0;

      let amount = (+nativeBalanceUsd! - (gasBuffer ?? 0)) * percent;

      // For SOLANA native token, deduct validator tip
      const isNativeSolana = tradeModeConfig.from.isNative && selectedNativeChain === SOLANA;
      if (isNativeSolana && nativeTokenPrice && validatorTip) {
        amount -= validatorTip * nativeTokenPrice;
      }

      let clampedAmount = Math.max(MIN_AMOUNT, amount);

      if (clampedAmount === 0) {
        setInputText("$0.00");
      } else {
        const newValue = `${+clampedAmount}`;
        setOriginalInputUsd(newValue);
        setInputUsdString(newValue);
        setHasBridgeFeeApplied(false); // Reset bridge fee flag when percent is pressed
        setIsInsufficientBalance(false);
      }

      setIsInsufficientBalance(
        clampedAmount === 0 ||
          +clampedAmount > +nativeBalanceUsd! ||
          (+nativeBalanceUsd! - (gasBuffer ?? 0)) * percent < 0,
      );
    } else {
      const balance = isTraddingWrappedToken
        ? getUserBalance(selectedTargetChain, "usd")
        : targetBalanceUsd;

      // For handling the 18 decimal places issue on EVMs 100% sell
      const newValue = `${+balance! * percent}`;
      if (percent === 1) {
        setIsSellAll(true);
      } else {
        setIsSellAll(false);
      }
      setOriginalInputUsd(newValue);
      setInputUsdString(newValue);
      setHasBridgeFeeApplied(false); // Reset bridge fee flag when percent is pressed
      setIsInsufficientBalance(false);
    }
  };

  useEffect(() => {
    setIsBalanceExceeded(false);

    if (inputText && isLoadingPriceImpact) {
      return setInputText("LOADING...");
    }

    if (bridgeFee && inputText) {
      setInputText(undefined);
    }

    if (
      mode === "BUY" &&
      selectedNativeChain !== selectedTargetChain &&
      bridgeFee !== 0 &&
      !hasBridgeFeeApplied
    ) {
      // check if has sufficient balance
      const cost = +originalInputUsd + (bridgeFee ?? 0) + (gasBuffer ?? 0);
      const isEnoughBalance = getUserBalance(selectedNativeChain, "usd")! >= cost;

      if (isEnoughBalance) {
        return;
      }

      // Use originalInputUsd to avoid infinite loop
      const newInputUsd = +originalInputUsd - bridgeFee;
      if (newInputUsd < 0) {
        setHasBridgeFeeApplied(true); // Set flag to prevent further iterations
        setIsInsufficientBalance(true);
        return setInputUsdString("0");
      }

      // Apply bridge fee deduction only once
      if (nativeTokenPrice && newInputUsd > 0) {
        setInputUsdString(newInputUsd.toString());
        setHasBridgeFeeApplied(true); // Set flag to prevent further iterations
      }
    } else if (mode === "SELL") {
      // check if has sufficient balance
      const cost = (bridgeFee ?? 0) + (gasBuffer ?? 0);
      const isEnoughBalance = getUserBalance(selectedTargetChain, "usd")! >= cost;

      if (!isEnoughBalance) {
        setIsInsufficientBalance(true);
        return setInputUsdString("0");
      }
    }
  }, [
    inputUsdString,
    originalInputUsd,
    isLoadingPriceImpact,
    selectedNativeChain,
    selectedTargetChain,
    targetAmount,
    nativeAmount,
    inputText,
    bridgeFee,
    mode,
    nativeTokenPrice,
    hasBridgeFeeApplied,
    gasBuffer,
    getUserBalance,
  ]);

  // Handle Confirm
  const handleConfirmClick = async (slippage?: number) => {
    try {
      const isEnoughBalance = getUserBalance(selectedNativeChain, "usd")! >= (gasBuffer ?? 0);

      if (!isEnoughBalance) {
        onFail?.("Insufficient balance to pay for gas");
        onProcessed?.();
        onClose?.();
        return;
      }

      handleSetIsTrading(true);
      showToast({
        variant: "info",
        message:
          "Your transaction is being broadcasted. This typically takes around 30 seconds. Please hold tight!",
      });
      onProcessing?.();
      onClose?.();

      let result;
      let chain = TON;
      // let masterAddress = targetTokenConfig[selectedTargetChain].token!;
      const initialNativeBalance = getUserBalance(selectedTargetChain, "amount")!;

      chain = tradeModeConfig.from.isNative ? selectedNativeChain : selectedTargetChain;
      const payload: {
        srcToken: string;
        destToken: string;
        senderAddress: string;
        nativeSpent: number;
        slippage: number;
        srcChain: string;
        destChain: string;
        tradeType: string;
        isSellAll: boolean;
        isBuyAll: boolean;
        priceUsd: number;
        tgGroupId?: number;
        isFromTrendingCall?: boolean;
        destinationAddress?: string;
        dstChainOrderAuthorityAddress?: string;
        isSwap?: boolean;
        tokenPrice?: number;
      } = {
        srcToken: tradeModeConfig.from.isNative
          ? CHAIN_CONFIG[selectedNativeChain].nativeTokenAddress!
          : targetTokenConfig[selectedTargetChain].token!,
        destToken: tradeModeConfig.to.isNative
          ? CHAIN_CONFIG[selectedNativeChain].nativeTokenAddress!
          : targetTokenConfig[selectedTargetChain].token!,
        senderAddress,
        nativeSpent: tradeModeConfig.from.isNative ? nativeAmount : targetAmount,
        slippage: slippage!,
        tradeType: mode,
        isSellAll,
        isBuyAll,
        srcChain: chain,
        destChain: tradeModeConfig.from.isNative ? selectedTargetChain : selectedNativeChain,
        priceUsd: +inputUsdString,
        tokenPrice: targetTokenPrice ?? undefined,
        ...(tgGroupId ? { tgGroupId } : {}),
        ...(isFromTrendingCall ? { isFromTrendingCall } : {}),
        ...(destinationAddress && selectedTargetChain !== selectedNativeChain
          ? { destinationAddress }
          : {}),
        ...(destinationAddress && selectedTargetChain !== selectedNativeChain
          ? { dstChainOrderAuthorityAddress: destinationAddress }
          : {}),
        ...(propsMode === "SWAP" ? { isSwap: true } : {}),
      };
      console.log("payload: ", payload);

      if (isCustodial) {
        result = await axiosService.crossChainTradeCustodial(payload);
      } else {
        result = await axiosService.crossChainTrade(payload);
      }

      // console.log("result: ", result);

      if (result.status < 300) {
        let callData;
        try {
          callData = JSON.parse(result.callData);
        } catch (e) {
          callData = { calldatas: result.callData };
        }
        // console.log("callData: ", callData);

        let hash;
        let signedTxn;
        let bundleId;

        if (isCustodial) {
          hash = result.hash;
          bundleId = result.bundleId;
        } else if (chain === SOLANA) {
          signedTxn = await signAndBroadcastSolanaTxn(senderAddress, result);
        } else {
          hash = await signAndBroadcastTxn(senderAddress, chain, callData);
        }
        // console.log("hash: ", hash);
        // console.log("signedTxn: ", signedTxn);
        // console.log("bundleId: ", bundleId);

        if (hash || signedTxn || bundleId) {
          setTxnHash(hash);

          if (propsMode !== "SWAP" || isCustodial || chain === SOLANA) {
            showToast({
              variant: "info",
              message: "Your transaction is broadcasted. Pending confirmation.",
            });
            // call update trade
            const res = await axiosService.updateTrade({
              hash,
              bundleId,
              signedTxn,
              tradeId: result.tradeId,
              isTransfer: propsMode === "SWAP",
            });

            let variant: "success" | "error" = "success";
            if (res.status >= 300) variant = "error";
            onProcessed?.();
            return showToast({ variant, message: res.message });
          }

          showToast({
            variant: "success",
            message: `Your transaction is executed. 
              <a target="_blank" href='${
                CHAIN_CONFIG[chain as Chain].blockExplorer
              }/${hash}'>Link</a>`,
          });
        }

        // Add polling logic for balance update
        const initialBalance = targetBalanceAmount;
        let attempts = 0;
        const maxAttempts = 10;
        const pollInterval = 3000; // 3 seconds

        setIsPolling(true);

        if (propsMode !== "SWAP") {
          setIsTonBoardcasting(tradeModeConfig.from.isNative);

          const pollForBalanceUpdate = async () => {
            while (attempts < maxAttempts) {
              const newBalance = await refetchTargetTokenBalance();
              if (newBalance?.data?.quantity !== initialBalance) {
                break;
              }
              attempts++;
              await new Promise((resolve) => setTimeout(resolve, pollInterval));
            }
          };

          await pollForBalanceUpdate();
          setIsPolling(false);
        } else {
          setIsTonBoardcasting(tradeModeConfig.from.isNative);

          const pollForNativeBalanceUpdate = async () => {
            while (attempts < maxAttempts) {
              const newBalance = await updateUserBalance();
              if (newBalance?.amount[selectedTargetChain] !== initialNativeBalance) {
                break;
              }
              attempts++;
              await new Promise((resolve) => setTimeout(resolve, pollInterval));
            }
          };

          await pollForNativeBalanceUpdate();
          setIsPolling(false);
        }

        onProcessed?.();
      } else {
        if (result.message.includes("Execution reverted")) {
          showToast({
            variant: "error",
            message: "Broadcast unsuccessful. Please retry.",
            duration: 10000,
          });
        } else {
          showToast({ variant: "error", message: result.message, duration: 10000 });
        }
        onFail?.(result.message);
        console.error("Transaction Failed: ", result);
        onProcessed?.();
        onClose?.();
      }
    } catch (error) {
      console.error(error);
      showToast({ variant: "error", message: "Transaction Failed", duration: 10000 });
      onFail?.("Something went wrong. Please try again.");
    } finally {
      handleSetIsTrading(false);
      updateUserBalance();

      if (location.pathname.includes("portfolio")) {
        refetchUserAllPortfolioAssets();
      }
    }
  };

  return {
    // Sum
    inputUsd: +inputUsdString,
    targetAmount: targetAmount,
    nativeAmount: nativeAmount,
    shouldConfirmDisabled:
      isInsufficientBalance || isTrading || isNativeBalanceLoading || !!inputText,
    isSellAll,
    isBuyAll,
    isOpenConfirmDrawer,
    handleOpenConfirmDrawer: handleOpenConfirmDrawer,
    onConfirm: handleConfirm,
    onNumpadPress: handleNumpadPress,
    onClose: handleOnClose,
    onPercentPress: handlePercentPress,
    priceHint: isInsufficientBalance ? <InsufficientPriceHint /> : null,
    setTargetAmount,
    setInputUsdString,
    setOriginalInputUsd,
    priceImpact,
    noValidRouteMessage,
    isLoadingPriceImpact,
    isRefetchingPriceImpact,
    refetchPriceImpact,
    isBalanceExceeded,
    setInputText,
    bridgeFee,
    inputText,
  };
};

export const useApproval = ({ targetAmount }: { targetAmount: number }) => {
  const { mode, targetTokenConfig, selectedNativeChain, selectedTargetChain, senderAddress } =
    useTradeContext();
  const tradeModeConfig = TRADING_MODE_CONFIG_MAP[mode];

  // Approval Logic
  const [tempAllowanceAmount, setTempAllowanceAmount] = useState(0);
  const [isApproving, setIsApproving] = useState(false);
  const [isApproveSucess, setIsApproveSucess] = useState(false);
  const { approveAllowance } = useTxnSign();

  const { showToast } = useToastStore();

  // Check if approval is needed to update
  useEffect(() => {
    // Reset approval status if token amount changes
    if (tempAllowanceAmount !== undefined) {
      if (tempAllowanceAmount < targetAmount) {
        resetAllowance();
      } else {
        setIsApproveSucess(true);
      }
    }
  }, [mode, targetAmount, tempAllowanceAmount]);

  // Fetch Current Allowance (EVMS Only)
  const {
    data: currentAllowanceAmount,
    refetch: refetchCurrentAllowance,
    isLoading: isGetCurrentAllowanceLoading,
  } = useQuery({
    queryKey: [
      "get-current-allowance",
      mode,
      CHAIN_CONFIG[selectedNativeChain]?.nativeTokenAddress,
      targetTokenConfig[selectedTargetChain]?.token,
    ],
    queryFn: () =>
      axiosService.getCurrentAllowance({
        srcToken: targetTokenConfig[selectedTargetChain].token!,
        destToken: CHAIN_CONFIG.ethereum.nativeTokenAddress,
        senderAddress: senderAddress,
        chain: selectedTargetChain,
      }),
    enabled: !!senderAddress && EVM_CHAINS.includes(selectedTargetChain) && mode === "SELL",
  });

  // Update Temp Allowance Amount from API
  useEffect(() => {
    if (currentAllowanceAmount !== undefined) {
      setTempAllowanceAmount(currentAllowanceAmount);
    } else {
      setTempAllowanceAmount(0);
    }
  }, [currentAllowanceAmount]);

  // Handle Approval
  const handleApproveAllowance = async () => {
    try {
      setIsApproving(true);
      showToast({ variant: "info", message: "Approving spending..." });

      const amount =
        targetAmount + (tradeModeConfig.approvalSpendingAmount[selectedNativeChain] ?? 0);

      const tokenAddress = tradeModeConfig.from.isNative
        ? CHAIN_CONFIG[selectedNativeChain].nativeTokenAddress!
        : targetTokenConfig[selectedTargetChain].token!;

      const res = await axiosService.approveAllowance({
        chain: selectedTargetChain,
        srcToken: tokenAddress,
        destToken: tradeModeConfig.to.isNative
          ? CHAIN_CONFIG[selectedNativeChain].nativeTokenAddress!
          : targetTokenConfig[selectedTargetChain].token!,
        senderAddress,
        amount,
      });

      if (res.hash) {
        showToast({
          variant: "success",
          message: "Spending approved. Please proceed to trade.",
        });
        setTempAllowanceAmount(amount);
        return true;
      }

      const spenderAddress = res.spenderAddress;

      if (spenderAddress) {
        const amountToApprove = res.amountToApprove;
        const hash = await approveAllowance(
          selectedTargetChain,
          tokenAddress,
          senderAddress,
          spenderAddress,
          amountToApprove,
        );

        if (hash) {
          showToast({
            variant: "success",
            message: "Spending approved. Please proceed to trade.",
          });
          setTempAllowanceAmount(amount);
          return true;
        }
      } else {
        showToast({ variant: "error", message: res.message, duration: 5000 });
        throw new Error("Approval failed: ", res.message);
      }
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      setIsApproving(false);
    }
  };

  // Reset Approval Status
  const resetAllowance = () => {
    setIsApproving(false);
    setIsApproveSucess(false);
  };

  return {
    isApproving,
    isApproveSucess,
    isGetCurrentAllowanceLoading,
    resetAllowance,
    setIsApproving,
    setIsApproveSucess,
    refetchCurrentAllowance,
    handleApproveAllowance,
  };
};

export const usePriceImpact = ({
  isSellAll,
  isBuyAll,
  targetAmount,
  nativeAmount,
}: {
  isSellAll: boolean;
  isBuyAll: boolean;
  targetAmount: number;
  nativeAmount: number;
}) => {
  const {
    mode: tradeMode,
    nativeTokenPrice,
    targetTokenPrice,
    selectedNativeChain,
    selectedTargetChain,
    targetTokenConfig,
    senderAddress,
    tgGroupId,
    destinationAddress,
  } = useTradeContext();

  const { getUserBalance } = useTradeStore();

  const { preferences = {} } = useUserStoreV2();
  const { slippage = 3, validatorTip } = preferences;
  const mode = tradeMode === "SWAP" ? "BUY" : tradeMode;
  const tradeModeConfig = TRADING_MODE_CONFIG_MAP[mode];

  // Price Impact
  const [priceImpact, setPriceImpact] = useState<number | null>(null);
  const [noValidRouteMessage, setNoValidRouteMessage] = useState<string | null>(null);
  // Signal
  const { getSignal } = useSignalAbort();

  const {
    data: priceImpactData,
    refetch: refetchPriceImpact,
    isLoading: isLoadingPriceImpact,
    isRefetching: isRefetchingPriceImpact,
  } = useQuery({
    queryKey: ["priceImpact", mode, nativeAmount, targetAmount, slippage],
    queryFn: () => {
      if ((mode === "BUY" && nativeAmount === 0) || (mode === "SELL" && +targetAmount === 0)) {
        return {
          inputAmount: 0,
          inputDecimals: 0,
          outputAmount: 0,
          outputDecimals: 0,
          bridgeFee: 0,
        };
      }

      let spent;
      if (mode === "BUY") {
        spent = nativeAmount;
      } else {
        spent = +targetAmount;
      }

      const srcChain = mode === "BUY" ? selectedNativeChain : selectedTargetChain;
      const destChain = mode === "BUY" ? selectedTargetChain : selectedNativeChain;

      const tokenTradePayload = {
        srcToken: tradeModeConfig.from.isNative
          ? CHAIN_CONFIG[selectedNativeChain].nativeTokenAddress!
          : targetTokenConfig[selectedTargetChain].token!,
        destToken: tradeModeConfig.to.isNative
          ? CHAIN_CONFIG[selectedNativeChain].nativeTokenAddress!
          : targetTokenConfig[selectedTargetChain].token!,
        senderAddress,
        nativeSpent: spent,
        slippage: slippage ?? 3,
        tradeType: mode,
        isSellAll,
        isBuyAll,
        srcChain,
        destChain,
        ...(tgGroupId ? { tgGroupId } : {}),
        ...(destinationAddress && srcChain !== destChain ? { destinationAddress } : {}),
        ...(destinationAddress && srcChain !== destChain
          ? { dstChainOrderAuthorityAddress: destinationAddress }
          : {}),
      };

      return axiosService.tokenTradePreview(tokenTradePayload, getSignal());
    },
    enabled:
      !!mode && !!selectedNativeChain && !!selectedTargetChain && !!slippage && !!senderAddress,
  });

  useEffect(() => {
    if (
      priceImpactData &&
      (priceImpactData.inputAmount === 0 || priceImpactData.outputAmount === 0)
    ) {
      setPriceImpact(null);
      setNoValidRouteMessage(null);
    }

    if (priceImpactData && priceImpactData.inputAmount) {
      const { inputAmount, inputDecimals, outputAmount, outputDecimals } = priceImpactData;

      const balance =
        getUserBalance(
          getChainsByMode(mode, selectedNativeChain, selectedTargetChain).from,
          "amount",
        ) ?? 0;

      let isEnoughBalance = true;

      if (mode === "BUY") {
        const srcToken = CHAIN_CONFIG[selectedNativeChain].nativeTokenAddress;

        if (srcToken === WRAPPED_NATIVE_TOKENS_ADDRESS.solana) {
          const jitoTip = validatorTip ?? 0;
          isEnoughBalance = inputAmount / 10 ** inputDecimals + jitoTip <= balance;
        } else {
          isEnoughBalance = inputAmount / 10 ** inputDecimals <= balance * 0.99;
        }
      }

      if (!isEnoughBalance) {
        return setNoValidRouteMessage("Insufficient balance to pay for gas");
      } else if (priceImpactData.message) {
        return setNoValidRouteMessage(priceImpactData.message);
      }

      let inputPrice, outputPrice;

      const nativePrice = nativeTokenPrice!;
      const tokenPrice = targetTokenPrice!;

      if (mode === "BUY") {
        inputPrice = (inputAmount / 10 ** inputDecimals) * nativePrice;
        outputPrice = (outputAmount / 10 ** outputDecimals) * tokenPrice;
      } else {
        inputPrice = (inputAmount / 10 ** inputDecimals) * tokenPrice;
        outputPrice = (outputAmount / 10 ** outputDecimals) * nativePrice;
      }

      const priceImpact = inputPrice - outputPrice;

      setPriceImpact(priceImpact);
      setNoValidRouteMessage(null);
    } else {
      if (priceImpactData && priceImpactData.status !== 200 && priceImpactData.message) {
        console.error(priceImpactData.message);
        setNoValidRouteMessage(priceImpactData.message);
        setPriceImpact(null);
      } else {
        if (priceImpactData && priceImpactData.message) {
          console.error(priceImpactData?.message);
        }
        setPriceImpact(null);
      }
    }
  }, [priceImpactData]);

  return {
    priceImpact,
    noValidRouteMessage,
    isLoadingPriceImpact,
    isRefetchingPriceImpact,
    refetchPriceImpact,
    bridgeFee: priceImpactData?.bridgeFee ?? 0,
  };
};
